import { useState } from "react";
import type { StepDetailProps } from "./index";
import { STEP_OUTPUT_SCHEMAS, zodIssuesToErrors } from "./schemas";
import { StepActions } from "./StepActions";
import { MarkdownText, hasMarkdown } from "../review/MarkdownText";

interface DecisionOption {
  id: string;
  label: string;
}

function readOptions(inputs: Record<string, unknown>): DecisionOption[] {
  const raw = inputs["options"];
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((o): o is DecisionOption => {
      if (!o || typeof o !== "object") return false;
      const r = o as Record<string, unknown>;
      return typeof r["id"] === "string" && typeof r["label"] === "string";
    })
    .map((o) => ({ id: o.id, label: o.label }));
}

export function DecisionDetail({
  step,
  play,
  workspaceId,
  currentUserId,
  persona,
}: StepDetailProps) {
  const options = readOptions(step.inputs);
  const question =
    typeof step.inputs["question"] === "string"
      ? (step.inputs["question"] as string)
      : step.title;
  const [chosen, setChosen] = useState<string>("");
  const [rationale, setRationale] = useState<string>("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  const buildOutput = (): Record<string, unknown> | null => {
    const parsed = STEP_OUTPUT_SCHEMAS["decision"].safeParse({
      chosen_option_id: chosen,
      rationale,
    });
    if (!parsed.success) {
      const map: Record<string, string> = {};
      for (const e of zodIssuesToErrors(parsed.error)) map[e.path] = e.message;
      setErrors(map);
      return null;
    }
    setErrors({});
    return parsed.data as Record<string, unknown>;
  };

  return (
    <div className="ho-detail ho-decision" data-testid="step-detail-decision">
      <div className="ho-eb">DECISION · STEP {step.ord + 1}</div>
      <h2 className="ho-h2">{step.title}</h2>
      <div className="ho-meta-row">
        <span className="pill amber">{step.state.replace("_", " ")}</span>
        {step.agentBindingId ? (
          <span className="attr-badge">
            <span className="glyph">◆</span> agent
          </span>
        ) : null}
      </div>

      {step.description ? (
        <div className="ho-rec" data-testid="decision-question">
          <div className="ho-rec-l">QUESTION</div>
          <div className="ho-rec-b">{question}</div>
          <div className="ho-rec-b" style={{ marginTop: 8 }}>
            {hasMarkdown(step.description) ? (
              <MarkdownText>{step.description}</MarkdownText>
            ) : (
              step.description
            )}
          </div>
        </div>
      ) : (
        <div className="ho-rec" data-testid="decision-question">
          <div className="ho-rec-l">QUESTION</div>
          <div className="ho-rec-b">{question}</div>
        </div>
      )}

      <div style={{ display: "grid", gap: 8, marginTop: 12 }}>
        <div style={{ fontSize: 11.5, color: "var(--text-3)", letterSpacing: ".14em" }}>
          OPTIONS
        </div>
        {options.length === 0 ? (
          <div style={{ fontSize: 12, color: "var(--text-3)" }}>
            No options on this step.
          </div>
        ) : (
          options.map((opt) => (
            <label
              key={opt.id}
              data-testid={`decision-option-${opt.id}`}
              style={{
                display: "flex",
                gap: 8,
                alignItems: "center",
                padding: "8px 10px",
                border: "1px solid var(--border)",
                borderRadius: 6,
                cursor: "pointer",
                background:
                  chosen === opt.id ? "var(--surface-2)" : "transparent",
              }}
            >
              <input
                type="radio"
                name={`decision-${step.id}`}
                value={opt.id}
                checked={chosen === opt.id}
                onChange={() => setChosen(opt.id)}
              />
              <span>{opt.label}</span>
            </label>
          ))
        )}
        {errors["chosen_option_id"] ? (
          <span
            style={{ color: "var(--danger)", fontSize: 12 }}
            data-testid="decision-error-option"
          >
            {errors["chosen_option_id"]}
          </span>
        ) : null}

        <label style={{ display: "block", marginTop: 8 }}>
          <span
            style={{
              fontSize: 11.5,
              color: "var(--text-3)",
              letterSpacing: ".14em",
            }}
          >
            RATIONALE
          </span>
          <textarea
            data-testid="decision-rationale"
            value={rationale}
            onChange={(e) => setRationale(e.target.value)}
            rows={3}
            style={{
              width: "100%",
              marginTop: 4,
              border: "1px solid var(--border)",
              borderRadius: 6,
              padding: 8,
              fontFamily: "inherit",
              fontSize: 13,
            }}
          />
        </label>
        {errors["rationale"] ? (
          <span
            style={{ color: "var(--danger)", fontSize: 12 }}
            data-testid="decision-error-rationale"
          >
            {errors["rationale"]}
          </span>
        ) : null}
      </div>

      <StepActions
        step={step}
        workspaceId={workspaceId}
        playId={play.id}
        campaignId={play.campaignId ?? null}        currentUserId={currentUserId}
        persona={persona}
        buildOutput={buildOutput}
      />
    </div>
  );
}
